import { useCallback, useEffect, useRef, useState } from "react"

import { CheckInHost } from "../../serviceCenterHomeApi"

export type CheckInAlertType = "success" | "error"

export type CheckInAlertItem = {
    id: string
    type: CheckInAlertType
    message: string
    host?: CheckInHost
}

type AddAlertOptions = {
    autoDismissMs?: number
    host?: CheckInHost
}

export type CheckInAlertsApi = Readonly<{
    alerts: CheckInAlertItem[]
    addAlert: (type: CheckInAlertType, message: string, options?: AddAlertOptions) => void
    removeAlert: (id: string) => void
}>

export function useCheckInAlerts(): CheckInAlertsApi {
    const [alerts, setAlerts] = useState<CheckInAlertItem[]>([])
    const alertsRef = useRef<CheckInAlertItem[]>([])
    const alertTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        alertsRef.current = alerts
    }, [alerts])

    const addAlert = useCallback((type: CheckInAlertType, message: string, options?: AddAlertOptions) => {
        const { autoDismissMs, host } = options ?? {}
        const id = Date.now().toString()

        // Behave like a notification stack: the newest alert goes on top, and an identical one replaces
        const withoutDuplicate = alertsRef.current.filter(
            (alert) => !(alert.type === type && alert.message === message),
        )
        alertsRef.current = [{ id, type, message, host }, ...withoutDuplicate]
        setAlerts(alertsRef.current)

        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current)
            alertTimeoutRef.current = null
        }
        if (autoDismissMs) {
            alertTimeoutRef.current = setTimeout(() => {
                setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id))
                alertTimeoutRef.current = null
            }, autoDismissMs)
        }
    }, [])

    const removeAlert = useCallback((id: string) => {
        setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id))
    }, [])

    useEffect(() => {
        return () => {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current)
            }
        }
    }, [])

    return { alerts, addAlert, removeAlert }
}
